<template>
  <info-pane
    ref="infoPane"
    :loading="!asset"
  >
    <confirmation-dialog ref="confirm" />
    <div
      v-if="asset"
      class="px-8 py-16"
    >
      <v-row>
        <v-col cols="1">
          <asset-icon :asset-type="asset.assetType" />
        </v-col>
        <v-col>
          <h2>
            {{ asset.assetName }}
          </h2>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <h4 class="subheader">
            Type
          </h4>
        </v-col>
        <v-col>
          <asset-type-label :asset-type="asset.assetType" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <h4 class="subheader">
            Details
          </h4>
        </v-col>
        <v-col>
          {{ asset.details }}
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <h4 class="subheader">
            Value
          </h4>
        </v-col>
        <v-col>
          <asset-value-display :asset="asset" />
        </v-col>
      </v-row>
      <v-row v-if="isAccountAdmin">
        <v-col>
          <info-pane-action-btn
            icon="bx-edit"
            label="Edit asset"
            @click="editAsset"
          />
        </v-col>
        <v-col>
          <info-pane-action-btn
            icon="bx-x-circle"
            label="Delete asset"
            @click="deleteAsset"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <h4 class="subheader">
            Associated documents
          </h4>
          <v-data-table
            :headers="documentHeaders"
            :items="documents"
            show-expand
          >
            <template #[`item.name`]="{ item }">
              <router-link :to="{ name: 'register.documents.info', params: { documentId: item.id }}">
                {{ item.name }}
              </router-link>
            </template>
            <template #[`item.documentType`]="{ item }">
              {{ documentTypeLabel(item) }}
            </template>
            <template #expanded-item="{ item }">
              <td colspan="2">
                Signed by: {{ signedByForDocument(item) }}
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <edit-asset-dialog ref="editAsset" />
    </div>
  </info-pane>
</template>

<script>
import InfoPane from './InfoPane.vue'
import AssetIcon from './AssetIcon.vue'
import AssetValueDisplay from './AssetValueDisplay.vue'
import InfoPaneActionBtn from './InfoPaneActionBtn.vue'
import EditAssetDialog from './EditAssetDialog.vue'
import ConfirmationDialog from './ConfirmationDialog.vue'
import AssetTypeLabel from './AssetTypeLabel.vue'

import { getDocumentType } from '@/dataAccess/documentTypes'
import { getAssetRef } from '@/dataAccess/assets'

export default {
  name: 'RegisterAssetsInfoPane',
  components: {
    AssetIcon,
    AssetTypeLabel,
    AssetValueDisplay,
    ConfirmationDialog,
    EditAssetDialog,
    InfoPane,
    InfoPaneActionBtn
  },
  props: {
    assetId: { type: String, required: true }
  },
  data: function () {
    return {
      documentHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Type', value: 'documentType' },
        { text: '', value: 'data-table-expand' }
      ]
    }
  },
  computed: {
    asset() {
      return this.getAssetById(this.assetId)
    },
    documents() {
      return this.getVaultDocumentsForAsset(this.asset)
    }
  },
  beforeCreate() {
    this.dispatchActions([
      'get_assets',
      'get_vaultDocuments',
      'get_counterpartyUsers'
    ])
    this.mapGetters([
      'getAssetById',
      'getVaultDocumentsForAsset',
      'isAccountAdmin'
    ])
  },
  methods: {
    signedByForDocument({ signatoryName, counterpartyName }) {
      return [signatoryName, counterpartyName].filter((item) => item).join(', ')
    },
    documentTypeLabel(document) {
      return getDocumentType(document.documentType).label
    },
    editAsset() {
      this.$refs.editAsset.open(this.asset)
    },
    async deleteAsset() {
      const name = this.asset.assetName
      const assetId = this.asset.assetId
      if (
        await this.$refs.confirm.open(
          `Delete ${name}?`,
          'Are you sure you want to delete this asset?'
        )
      ) {
        this.$handlingError(`Error deleting asset "${name}" (${assetId})`, async () => {
          this.$refs.infoPane.close()
          await getAssetRef(this.asset.id).delete()
          this.$showSuccessbar(`Deleted asset "${name}" (${assetId})`)
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.subheader {
  text-transform: uppercase;
  color: var(--v-darkgray-base);
}
</style>
